import { useState, useEffect, useCallback, useRef } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useIndexedDB } from '@thd-olt-global/thd-storage-utils';
import { getDefaultSession, getInitialMessage, getSessionId, sessionIsExpired } from '../utils/session-utils';
import { useGenerateQuestions } from './useGenerateQuestions';

/**
 * @param { boolean } withPersistence - Flag to check if persistence is desired
 * @returns { UseMessageHistoryReturn }
 */
export const useMessageHistory = (withPersistence = true) => {
  const isInitialSuggestedQuestionsEnabled = useConfigService('magic-apron-initial-suggested-questions-enabled');
  const session = useRef(null);
  const maBroadcastChannel = useRef(null);
  const isStorageAvailable = useRef(false);
  const wereQuestionsGenerated = useRef(false);
  const [messages, setMessages] = useState([getInitialMessage()]);
  const [isLoading, setIsLoading] = useState(false);

  /* eslint-disable camelcase */
  const {
    getByKey: iDBGetByKey_session,
    getAll: iDBGetAll_session,
    add: iDBAdd_session,
    cursor: iDBCursor_session,
  } = useIndexedDB('sessions');
  const {
    getAll: iDBGetAll_messages,
    add: iDBAdd_message,
    update: iDBUpdate_message,
    clear: iDBClear_messages,
    cursor: iDBCursor_message,
  } = useIndexedDB('messages');
  /* eslint-enable camelcase */

  const [submitToGenerateQuestions, {
    data: suggestedQuestionsResponse,
    loading: isSuggestedQuestionsLoading,
    error: suggestedQuestionsError,
  }] = useGenerateQuestions();

  const loadIDBMessages = () => {
    setIsLoading(true);
    return iDBGetAll_messages()
      .then((savedMessages) => {
        const sortedMessages = [...savedMessages].sort((first, second) => {
          if (first.isInitialMessage) return -1;
          if (second.isInitialMessage) return 1;
          return 0;
        });
        setMessages(sortedMessages);
        setIsLoading(false);
        return savedMessages;
      });
  };

  const handleSaveMessage = async (newMessage) => {
    const updatedMessage = {
      ...newMessage,
      sessionId: session.current.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
      isInitialMessage: false,
    };
    if (!isStorageAvailable.current) {
      setMessages([...messages, updatedMessage]);
      return Promise.resolve(updatedMessage);
    }
    if (!session.current.sessionId) return Promise.reject(new Error('Session ID not available'));

    return iDBAdd_message(updatedMessage)
      .then(loadIDBMessages)
      .then((savedMessages) => savedMessages.at(-1));
  };

  // Debounce function to limit the frequency of clearHistory calls
  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const clearMessageHistory = () => {
    iDBCursor_message((event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (!cursor.value.isInitialMessage) {
          cursor.delete();
        }
        cursor.continue();
      }
    }).then(loadIDBMessages);
  };

  const clearHistory = debounce(clearMessageHistory, 5); // 5ms delay

  const setFeedbackProvided = async (message) => {
    const updatedMessage = JSON.parse(JSON.stringify(message));
    updatedMessage.feedbackProvided = true;

    await iDBUpdate_message(updatedMessage);
  };

  const broadCastMessageUpdate = () => {
    if (maBroadcastChannel.current) {
      maBroadcastChannel.current.postMessage('magic-apron-new-message');
    }
  };

  const cleanIndexDb = async () => {
    // Remove expired sessions
    await iDBCursor_session((event) => {
      const cursor = event.target.result;

      if (cursor) {
        if (!cursor.value.sessionId || sessionIsExpired(cursor.value)) {
          cursor.delete();
        }
        cursor.continue();
      }
    });

    // Remove messages that have no session associated in db
    const retrievedSessions = await iDBGetAll_session();
    const retrievedSessionIds = retrievedSessions.map(({ sessionId }) => sessionId);

    await iDBCursor_message((event) => {
      const cursor = event.target.result;

      if (cursor) {
        if (!retrievedSessionIds.includes(cursor.value.sessionId)) {
          cursor.delete();
        }
        cursor.continue();
      }
    });
  };

  const init = async () => {
    const savedSession = await iDBGetByKey_session(getSessionId());
    let activeSession = savedSession;

    if (!activeSession) {
      const newSession = getDefaultSession();
      await iDBAdd_session(newSession);
      activeSession = newSession;
    }
    session.current = activeSession;
    isStorageAvailable.current = withPersistence && typeof indexedDB !== 'undefined';
    await cleanIndexDb();

    // We were not saving initial question in db before
    // So it is possible that an existing session would have no messages to display
    // Or it has messages but no initialMessage
    // The savedMessages.find condition can be removed once this code has sat in prod for a little bit
    const savedMessages = await iDBGetAll_messages();
    if (!savedSession || !savedMessages.find(({ isInitialMessage }) => isInitialMessage)) {
      await iDBAdd_message(getInitialMessage())
        .then(() => {
          if (isInitialSuggestedQuestionsEnabled) {
            submitToGenerateQuestions();
          }
        });
    }

    wereQuestionsGenerated.current = true;

    await loadIDBMessages();
  };

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      maBroadcastChannel.current = new BroadcastChannel('magic-apron-new-message');
    }

    init()
      .then(() => {
        if (maBroadcastChannel.current) {
          maBroadcastChannel.current.onmessage = loadIDBMessages;
        }
      })
      .catch(() => {});

    return (() => {
      if (maBroadcastChannel.current) {
        maBroadcastChannel.current.close();
      }
    });
  }, []);

  useEffect(() => {
    async function commitInitialSuggestedQuestions() {
      if (!isSuggestedQuestionsLoading && !suggestedQuestionsError && suggestedQuestionsResponse) {
        const dbMessages = await iDBGetAll_messages();
        const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);

        const generatedSuggestedQuestions = suggestedQuestionsResponse?.generateQuestions?.suggestedQuestions || [];
        initialMessage.content.suggestedQuestions = generatedSuggestedQuestions;

        await iDBUpdate_message(initialMessage)
          .then(loadIDBMessages)
          .finally(() => { broadCastMessageUpdate(); });
      }
    }

    commitInitialSuggestedQuestions().catch(() => {});
  }, [isSuggestedQuestionsLoading, suggestedQuestionsResponse]);

  return {
    messages,
    saveMessage: handleSaveMessage,
    historyLoading: isLoading,
    isSuggestedQuestionsLoading,
    wereQuestionsGenerated: wereQuestionsGenerated.current,
    clearHistory,
    setFeedbackProvided,
    broadCastMessageUpdate,
  };
};
